import React, { useEffect, useState } from "react";
import serviceApi from "../../../services/serviceApi";
import environment from "../../../environments/environment";
import defaultImage from "../../../assets/default.png";
import FamilyList from "./FamilyList";
import DocumentList from "./DocumentList";

interface FamilyMember {
    id?: string;
    name?: string;
    relation?: string;
    occupation?: string;
    phone?: string;
    imageUrl?: string;
}

interface DocumentItem {
    id?: string;
    name?: string;
    status?: string;
    submittedDate?: string;
}

interface StudentProfile {
    name?: string;
    class?: string;
    course?: string;
    adminNo?: string;
    photoUrl?: string;
    contactNo?: string;
    address?: string;
}

const ProfileContent: React.FC = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [studentData, setStudentData] = useState<StudentProfile>({});
    const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [showFamily, setShowFamily] = useState(true);
    const [showDocuments, setShowDocuments] = useState(true);

    useEffect(() => {
        loadProfileData();
    }, []);

    const loadProfileData = async () => {
        setIsLoading(true);

        try {
            const backendRaw = localStorage.getItem("backendResponse");
            if (backendRaw) {
                const backend = JSON.parse(backendRaw);
                const profile = backend?.objectData?.students?.[0];
                if (profile) {
                    setStudentData({
                        name: profile?.name,
                        class: profile?.clss,
                        course: profile?.course || "",
                        adminNo: profile?.sid,
                        photoUrl: profile?.imageUrl,
                        contactNo: profile?.phone1,
                        address: profile?.address || ""
                    });
                }
            }
            const familyResponse = await serviceApi.get(environment.familydetails);
            if (familyResponse?.status === "SUCCESS") {
                setFamilyMembers(
                    (familyResponse?.objectData?.familyMembers || []).map(
                        (m: any) => ({
                            id: m.id,
                            name: m.name,
                            relation: m.relation,
                            occupation: m.profession,
                            phone: m.phone,
                            imageUrl: m.imageUrl
                        })
                    )
                );
            }
            const documentResponse = await serviceApi.get(environment.enclosers);
            if (documentResponse?.status === "SUCCESS") {
                setDocuments(
                    (documentResponse?.objectData || []).map((d: any) => ({
                        id: d.id,
                        name: d.encloserName,
                        status: d.submitted ? "APPROVED" : "PENDING",
                        submittedDate: d.submittedDate || "",
                    }))
                );
            }
        } catch (error) {
            console.log("Profile loading error", error);
        } finally {
            setIsLoading(false);
        }
    };

    const getStatusBg = (status: string) => {
        const x = status?.toLowerCase();
        if (x === "pending") return "#e63946";
        if (x === "approved") return "var(--colorPrimaryDark)";
        if (x === "rejected") return "var(--colorAccentDark)";
        return "var(--colorPrimaryDark)";
    };

    return (
        <div className="notification-page">
            <h2
                className="title-text notification-heading"
                style={{ textAlign: "center" }}>
                Profile
            </h2>
            <div
                className="event-container"
                style={{ padding: "14px", borderRadius: "16px" }}>
                {isLoading && (
                    <div className="loading-overlay">
                        <div className="loading-overlay__content">
                            <div className="spinner spinner--lg"></div>
                            <span className="loading-text">
                                Loading profile...
                            </span>
                        </div>
                    </div>
                )}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: "12px"
                }}>
                    <div style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "12px",
                        flex: 1
                    }}>
                        <img
                            src={
                                studentData.photoUrl?.trim()
                                    ? studentData.photoUrl
                                    : defaultImage
                            }
                            onError={(e) =>
                            ((e.currentTarget as HTMLImageElement).src =
                                defaultImage)
                            }
                            style={{
                                width: "60px",
                                height: "70px",
                                borderRadius: "50%",
                                objectFit: "cover",
                                border: "2px solid #eee"
                            }}
                        />
                        <div>
                            <div style={{
                                fontSize: "18px",
                                fontWeight: 700,
                                color: "var(--colorPrimaryDark)"
                            }}>
                                {studentData.name}
                            </div>
                            <div style={{
                                marginTop: "4px",
                                fontSize: "14px",
                                color: "#666"
                            }}>
                                {studentData.class}
                                {studentData.course ? ` | ${studentData.course}` : ""}
                            </div>
                            <div style={{
                                marginTop: "4px",
                                fontSize: "13px",
                                color: "#777"
                            }}>
                                Admin No :
                                <b style={{ marginLeft: "4px" }}>
                                    #{studentData.adminNo}
                                </b>
                            </div>
                        </div>
                    </div>
                </div>
                <div style={{
                    borderTop: "1px solid #ececec",
                    margin: "14px 0"
                }} />

                {/* CONTACT + ADDRESS LEFT | UPDATE RIGHT */}
                <div style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-end"
                }}>
                    <div style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "10px"
                    }}>
                        <div style={{ fontSize: "14px", color: "#555" }}>
                            <b style={{ color: "var(--colorPrimaryDark)" }}>
                                Contact:
                            </b>{" "}
                            {studentData.contactNo || "N/A"}
                        </div>
                        <div style={{ fontSize: "14px", color: "#555" }}>
                            <b style={{ color: "var(--colorPrimaryDark)" }}>
                                Address:
                            </b>{" "}
                            {studentData.address || "N/A"}
                        </div>
                    </div>
                    <button
                        style={{
                            border: "none",
                            background: "var(--colorPrimaryDark)",
                            color: "#fff",
                            borderRadius: "20px",
                            padding: "8px 18px",
                            fontWeight: 600,
                            cursor: "pointer"
                        }}
                    >
                        Update
                    </button>
                </div>
            </div>
            <FamilyList
                members={familyMembers}
                showFamily={showFamily}
                setShowFamily={setShowFamily}
            />
            <DocumentList
                documents={documents}
                showDocuments={showDocuments}
                setShowDocuments={setShowDocuments}
                getStatusBg={getStatusBg}
            />
        </div>
    );
};

export default ProfileContent;